// Rules for VALIDATION_CONSTRAINTS.md section 1 — per-node integrity.
//
// The editor stores node ids as strings (vs engine integer ids). Uniqueness,
// type registration, reference resolution, and label/position integrity are
// enforced here. Engine-side only `UniqueNodeIds` + `UniqueNodeKeys` currently
// run, so these editor rules are the effective gate.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import { getFamily, labelOf } from '../helpers';
import { getNodeTypes } from '../../nodeRegistry';

const rule_1_1_NodeIdUnique: ValidationRule = {
  id: '1.1',
  description: 'Node Id must be present and unique within a process',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    const seen = new Map<string, number>();
    for (const n of process.nodes) {
      if (!n.id || typeof n.id !== 'string' || n.id.length === 0) {
        issues.push({
          severity: 'error',
          rule: '1.1',
          message: `${labelOf(n)} has missing or empty Id.`,
          nodeIds: n.id ? [n.id] : [],
        });
        continue;
      }
      seen.set(n.id, (seen.get(n.id) ?? 0) + 1);
    }
    for (const [id, count] of seen) {
      if (count > 1) {
        issues.push({
          severity: 'error',
          rule: '1.1',
          message: `Node Id "${id}" is used ${count} times; ids must be unique within a process.`,
          nodeIds: [id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_1_NodeIdUnique);

const rule_1_2_NodeKeyUnique: ValidationRule = {
  id: '1.2',
  description: 'Node Key must be unique within a process when non-empty',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    // pssHelper writes node.id into engine Key, so Key uniqueness usually
    // reduces to 1.1. If a user explicitly sets a distinct `data.key`
    // (uncommon), enforce uniqueness here. Empty/undefined keys are skipped.
    const issues: ValidationIssue[] = [];
    const keyToNodes = new Map<string, string[]>();
    for (const n of process.nodes) {
      const key = (n.data as any)?.key;
      if (typeof key !== 'string' || key.length === 0) continue;
      const list = keyToNodes.get(key) ?? [];
      list.push(n.id);
      keyToNodes.set(key, list);
    }
    for (const [key, ids] of keyToNodes) {
      if (ids.length > 1) {
        issues.push({
          severity: 'error',
          rule: '1.2',
          message: `Node Key "${key}" is used by ${ids.length} nodes; keys must be unique within a process.`,
          nodeIds: ids,
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_2_NodeKeyUnique);

const rule_1_3_NodeTypeRegistered: ValidationRule = {
  id: '1.3',
  description: 'Node type must be one of the registered node types',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    const registeredTypes = new Set(Object.keys(getNodeTypes()));
    for (const n of process.nodes) {
      if (!n.type || !registeredTypes.has(n.type)) {
        issues.push({
          severity: 'error',
          rule: '1.3',
          message: `${labelOf(n)} has unknown type "${n.type ?? '<missing>'}" — not in registered node types.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_3_NodeTypeRegistered);

const rule_1_4_NodeReferencesResolve: ValidationRule = {
  id: '1.4',
  description: 'Edge endpoints and parentNode references must point to existing nodes',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    const ids = new Set(process.nodes.map(n => n.id));
    for (const e of process.edges) {
      if (!ids.has(e.source)) {
        issues.push({
          severity: 'error',
          rule: '1.4',
          message: `Edge ${e.id} references missing source node "${e.source}".`,
          edgeIds: [e.id],
        });
      }
      if (!ids.has(e.target)) {
        issues.push({
          severity: 'error',
          rule: '1.4',
          message: `Edge ${e.id} references missing target node "${e.target}".`,
          edgeIds: [e.id],
        });
      }
    }
    for (const n of process.nodes) {
      const parent = (n as any).parentNode;
      if (typeof parent === 'string' && parent.length > 0 && !ids.has(parent)) {
        issues.push({
          severity: 'error',
          rule: '1.4',
          message: `${labelOf(n)} references missing parentNode "${parent}".`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_4_NodeReferencesResolve);

const rule_1_5_ActivityLabelRequired: ValidationRule = {
  id: '1.5',
  description: 'Activity must have a non-empty label (used as engine Key for runtime correlation)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFamily(n.type) !== 'Activity') continue;
      const label = n.data?.label;
      if (typeof label !== 'string' || label.trim().length === 0) {
        issues.push({
          severity: 'error',
          rule: '1.5',
          message: `${labelOf(n)} is an Activity and must have a non-empty label.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_5_ActivityLabelRequired);

const rule_1_6_NodeLabelPresent: ValidationRule = {
  id: '1.6',
  description: 'Every node should have a non-empty label',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      // Skip Activities — rule 1.5 already emits an error for them.
      if (getFamily(n.type) === 'Activity') continue;
      const label = n.data?.label;
      if (typeof label !== 'string' || label.trim().length === 0) {
        issues.push({
          severity: 'warning',
          rule: '1.6',
          message: `${labelOf(n)} has no label — add one for readability.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_6_NodeLabelPresent);

const rule_1_7_NodePositionFinite: ValidationRule = {
  id: '1.7',
  description: 'Node position.x and position.y must be finite numbers',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      const pos = (n as any).position;
      const x = pos?.x;
      const y = pos?.y;
      if (typeof x !== 'number' || !Number.isFinite(x) || typeof y !== 'number' || !Number.isFinite(y)) {
        issues.push({
          severity: 'error',
          rule: '1.7',
          message: `${labelOf(n)} has invalid position (x=${x}, y=${y}); coordinates must be finite numbers.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_7_NodePositionFinite);

// Resolve effective actorType for a node — child nodes (boundary events
// attached to a parent task/activity) inherit from their parent since they
// are physically anchored to it and not directly placed in a swimlane.
const effectiveActorType = (n: any, nodesById: Map<string, any>): string | undefined => {
  const own = n?.data?.actorType;
  if (typeof own === 'string' && own.trim().length > 0) return own.trim();
  const parentId = n?.parentNode;
  if (parentId) {
    const parent = nodesById.get(parentId);
    if (parent) return effectiveActorType(parent, nodesById);
  }
  return undefined;
};

const rule_1_8_ActorTypeRequired: ValidationRule = {
  id: '1.8',
  description: 'Every node must define a non-empty actorType (swimlane linkage, task routing)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    // Skip when no swimlanes defined. Export assigns the implicit default
    // "Actor" to every node in that case, so flagging missing actorType
    // would be noise — the user hasn't modelled lanes yet.
    if (!Array.isArray(process.swimlanes) || process.swimlanes.length === 0) return [];
    const issues: ValidationIssue[] = [];
    const byId = new Map(process.nodes.map(n => [n.id, n]));
    for (const n of process.nodes) {
      const actor = effectiveActorType(n, byId);
      if (!actor) {
        issues.push({
          severity: 'error',
          rule: '1.8',
          message: `${labelOf(n)} has no actorType — every node must have an actor assigned.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_8_ActorTypeRequired);

const rule_1_9_ActorTypePascalCase: ValidationRule = {
  id: '1.9',
  description: 'actorType must begin with an uppercase letter (A-Z)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    // Same rationale as 1.8 — no lanes, no actorType to constrain.
    if (!Array.isArray(process.swimlanes) || process.swimlanes.length === 0) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      // Only check explicit actorTypes — inherited ones already validated
      // on the owning parent, so don't double-report.
      const actorType = (n.data as any)?.actorType;
      if (typeof actorType !== 'string' || actorType.length === 0) continue;
      const first = actorType.charAt(0);
      if (first < 'A' || first > 'Z') {
        issues.push({
          severity: 'error',
          rule: '1.9',
          message: `${labelOf(n)} actorType "${actorType}" must start with an uppercase letter.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_1_9_ActorTypePascalCase);
